"use client";

/**
 * AdminImage — 클릭하면 확대(라이트박스)되는 이미지
 *
 * 썸네일을 보여주고, 클릭하면 전체화면 오버레이에 큰 이미지를 띄운다.
 * 오버레이/X 버튼/Esc 로 닫는다. (Radix Dialog 기반 — 접근성·포커스 처리 포함)
 *
 * 예외 처리:
 * - src 가 없으면 "이미지 없음" placeholder 표시 (확대 비활성)
 * - 이미지 로드 실패 시 "이미지를 불러올 수 없습니다" 표시 (확대 비활성)
 *
 * @example
 * <AdminImage src={thumbUrl} alt="상품" className="size-12 rounded-md" />
 * <AdminImage src={thumbUrl} previewSrc={fullUrl} className="size-16 rounded" />
 */
import { type ComponentProps, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ImageOff, X, ZoomIn } from "lucide-react";

import { cn } from "../../lib/utils";

export interface AdminImageProps
  extends Omit<ComponentProps<"img">, "onClick" | "src"> {
  /** 이미지 URL (없으면 "이미지 없음" 표시) */
  src?: string | null;
  alt?: string;
  /** 확대 시 보여줄 큰 이미지 URL (기본: src) */
  previewSrc?: string;
  /** 클릭 확대 사용 (기본 true) */
  preview?: boolean;
  /**
   * 마우스 오버 시 확대 아이콘 오버레이 표시 (기본 true).
   * 테이블 셀처럼 마우스가 자주 지나가는 좁은 영역에서는 false로 꺼서
   * 스쳐 지나갈 때마다 오버레이가 반응하는 게 거슬리지 않게 할 수 있다.
   * (클릭 확대 자체는 preview 로 계속 켜져 있다 — 이 prop 은 hover 표시만 끈다)
   */
  hoverOverlay?: boolean;
  /** 이미지가 없을 때 문구 (기본 "이미지 없음") */
  noImageText?: string;
  /** 로드 실패 시 문구 (기본 "이미지를 불러올 수 없습니다") */
  errorText?: string;
}

export function AdminImage({
  src,
  alt = "",
  previewSrc,
  preview = true,
  hoverOverlay = true,
  noImageText = "이미지 없음",
  errorText = "이미지를 불러올 수 없습니다",
  className,
  ...props
}: AdminImageProps) {
  const [open, setOpen] = useState(false);
  // 에러가 난 src 를 기억 → src 가 바뀌면 자동으로 에러 상태가 풀린다(effect 불필요).
  const [errorSrc, setErrorSrc] = useState<string | null>(null);
  const errored = !!src && errorSrc === src;

  // 이미지 없음 / 로드 실패 → placeholder (확대 비활성)
  if (!src || errored) {
    const msg = !src ? noImageText : errorText;
    return (
      <div
        title={msg}
        aria-label={msg}
        className={cn(
          "flex flex-col items-center justify-center gap-0.5 overflow-hidden bg-muted text-muted-foreground",
          className
        )}
      >
        <ImageOff className="size-4 shrink-0" />
        <span className="max-w-full truncate px-1 text-center text-[10px] leading-tight">
          {msg}
        </span>
      </div>
    );
  }

  if (!preview) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt}
        onError={() => setErrorSrc(src)}
        className={className}
        {...props}
      />
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative inline-flex cursor-zoom-in overflow-hidden rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="이미지 확대"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          onError={() => setErrorSrc(src)}
          className={cn("object-cover", className)}
          {...props}
        />
        {hoverOverlay && (
          <span className="absolute inset-0 hidden items-center justify-center bg-black/30 text-white group-hover:flex">
            <ZoomIn className="size-5" />
          </span>
        )}
      </button>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 outline-none"
          >
            <DialogPrimitive.Title className="sr-only">
              이미지 확대 보기
            </DialogPrimitive.Title>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={previewSrc ?? src}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-[90vw] object-contain data-[state=open]:animate-in data-[state=open]:zoom-in-95"
            />
            <DialogPrimitive.Close className="absolute top-4 right-4 rounded-full bg-black/50 p-2 text-white outline-none transition-colors hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white">
              <X className="size-5" />
              <span className="sr-only">닫기</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
